import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CreditCard, Hash, Mail, MapPin, Phone, User } from "lucide-react";
import { createPublicMerchant } from "../actions/merchantActions";

const API = process.env.REACT_APP_API_URL;

const validators = {
  name: v => /^[A-Za-z][A-Za-z\s.'-]{1,48}$/.test(v.trim()) ? "" : "Name: letters only (2–49 chars).",
  contactNumber: v => /^\d{10}$/.test(v.trim()) ? "" : "Enter a 10-digit mobile number.",
  email: v => !v.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()) ? "" : "Enter a valid email address.",
  location: v => !v.trim() || /^[A-Za-z0-9][A-Za-z0-9\s.,'/-]{1,99}$/.test(v.trim()) ? "" : "Enter a valid location.",
  pincode: v => !v.trim() || /^\d{6}$/.test(v.trim()) ? "" : "Enter a valid 6-digit pincode.",
  upiId: v => !v.trim() || /^[\w.\-]{2,256}@[a-zA-Z]{2,64}$/.test(v.trim()) ? "" : "Enter a valid UPI ID (e.g. name@bank).",
};

const inputBase = 'w-full border rounded-lg pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';
const fieldIcons = {
  name: User,
  contactNumber: Phone,
  email: Mail,
  location: MapPin,
  pincode: Hash,
  upiId: CreditCard,
};

function IconInput({ field, className, ...props }) {
  const Icon = fieldIcons[field];

  return (
    <div className="relative">
      <input className={className} {...props} />
      {Icon && (
        <Icon
          size={18}
          strokeWidth={2}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
      )}
    </div>
  );
}

export default function FormGate() {
  const { token } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("");
  const [error, setError] = useState("");
  const [qrInfo, setQrInfo] = useState(null);
  const [form, setForm] = useState({ name: "", contactNumber: "", email: "", location: "", pincode: "", upiId: "" });
  const [touched, setTouched] = useState({});

  const errors = useMemo(() => {
    const out = {};
    for (const k of Object.keys(form)) { if (validators[k]) out[k] = validators[k](form[k]); }
    return out;
  }, [form]);

  const isValid = useMemo(
    () => Object.values(errors).every(e => e === "") && !!form.name.trim() && !!form.contactNumber.trim(),
    [errors, form.name, form.contactNumber]
  );

  const handleChange = field => e => {
    const val = e.target.value;
    setForm(prev => {
      switch (field) {
        case "contactNumber": case "pincode": return { ...prev, [field]: val.replace(/\D/g, "") };
        case "name": return { ...prev, [field]: val.replace(/[^A-Za-z\s.'-]/g, "") };
        case "location": return { ...prev, [field]: val.replace(/[^A-Za-z0-9\s.,'/-]/g, "") };
        default: return { ...prev, [field]: val };
      }
    });
  };

  const handleBlur = field => setTouched(t => ({ ...t, [field]: true }));

  const tryGetMerchant = async () => {
    try {
      const { data } = await axios.get(`${API}/merchants/qr/${token}`);
      if (data?.merchant) { setMode("enquiry"); setLoading(false); return true; }
      return false;
    } catch { return false; }
  };

  const resolveToken = async () => {
    try {
      const { data } = await axios.get(`${API}/qrs/${token}/resolve`);
      setQrInfo(data);
      if (data.status === "available") setMode("link");
      else if (data.status === "linked" && (!data.merchant || data.merchant.status !== "active")) setMode("inactive");
      else if (data.status === "linked" && data.merchant?.status === "active") setMode("enquiry");
      else if (data.status === "retired") setMode("retired");
      else { setMode("error"); setError("Unexpected QR state"); }
    } catch (e) {
      setMode("error");
      setError(e?.response?.data?.message || e.message || "Failed to resolve QR");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => { setLoading(true); setError(""); const ok = await tryGetMerchant(); if (!ok) await resolveToken(); })();
  }, [token]);

  const submitLink = async e => {
    e.preventDefault();
    setTouched(Object.keys(form).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
    if (!isValid) return;
    try {
      await dispatch(createPublicMerchant({ ...form, name: form.name.trim(), qrNumber: Number(qrInfo.number) }));
      window.location.reload();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to link QR");
    }
  };

  if (loading) return (
    <div className="p-8 flex justify-center">
      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (mode === "enquiry") { navigate(`/enquiry/${token}`); return null; }

  if (mode === "inactive") return (
    <div className="p-8 max-w-md mx-auto">
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
        <p className="text-yellow-700 font-medium mb-2">Merchant is created but not active yet</p>
        <p className="text-yellow-600 text-sm">Ask admin to approve this merchant before taking enquiries.</p>
      </div>
    </div>
  );

  if (mode === "retired") return (
    <div className="p-8 max-w-md mx-auto">
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
        <p className="text-gray-700 font-medium mb-2">This QR is retired</p>
        <p className="text-gray-500 text-sm">Please use a fresh QR from the pool.</p>
      </div>
    </div>
  );

  if (mode === "link") {
    const iCls = field => `${inputBase} ${touched[field] && errors[field] ? 'border-red-400' : 'border-gray-300'}`;
    const Help = ({ field }) => touched[field] && errors[field]
      ? <p className="text-xs text-red-600 mt-1">{errors[field]}</p>
      : null;

    return (
      <div className="px-3 py-4 sm:p-6 bg-gray-50 min-h-screen">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Link QR to New Merchant</h1>
          <p className="text-sm text-gray-500 mb-6">
            QR Number: <span className="font-semibold text-gray-700">{qrInfo?.number}</span> &nbsp;·&nbsp; Token: <span className="font-mono text-gray-600">{token}</span>
          </p>

          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <form onSubmit={submitLink} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name (Team Member Name) *</label>
                <IconInput field="name" className={iCls("name")} placeholder="Team member name" value={form.name} onChange={handleChange("name")} onBlur={() => handleBlur("name")} />
                <Help field="name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                <IconInput field="contactNumber" className={iCls("contactNumber")} placeholder="10-digit number" value={form.contactNumber} onChange={handleChange("contactNumber")} onBlur={() => handleBlur("contactNumber")} maxLength={10} inputMode="numeric" />
                <Help field="contactNumber" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <IconInput field="email" className={iCls("email")} placeholder="Email" type="email" value={form.email} onChange={handleChange("email")} onBlur={() => handleBlur("email")} />
                <Help field="email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <IconInput field="location" className={iCls("location")} placeholder="Location" value={form.location} onChange={handleChange("location")} onBlur={() => handleBlur("location")} />
                <Help field="location" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                <IconInput field="pincode" className={iCls("pincode")} placeholder="6-digit pincode" value={form.pincode} onChange={handleChange("pincode")} onBlur={() => handleBlur("pincode")} maxLength={6} inputMode="numeric" />
                <Help field="pincode" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UPI ID</label>
                <IconInput field="upiId" className={iCls("upiId")} placeholder="e.g. name@bank" value={form.upiId} onChange={handleChange("upiId")} onBlur={() => handleBlur("upiId")} />
                <Help field="upiId" />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button type="submit" disabled={!isValid}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-6 py-2.5 rounded-lg transition-colors">
                  Link &amp; Continue
                </button>
              </div>
            </form>
            {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-md mx-auto">
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600 text-sm">{error || "Merchant not found or inactive"}</p>
      </div>
    </div>
  );
}
